import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { api } from '../../../shared/api';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ContractCases from './ContractCases';

const CONDITION_OPTIONS = ['good', 'fair', 'damaged', 'written_off'];

const ContractReturn = ({ contractId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [returning, setReturning] = useState(false);
  const [message, setMessage] = useState(null);
  // Per-unit return data: { [unit_id]: { condition, notes, location_note, selected } }
  const [returnData, setReturnData] = useState({});

  const { data: deployedUnits = [], isLoading: loading } = useQuery({
    queryKey: ['contract-deployed-units', contractId],
    queryFn: async () => {
      const res = await api.get(`contracts/api/contracts/${contractId}/deployed_units/`);
      return res.data || res;
    },
  });

  useEffect(() => {
    setReturnData(prev => {
      const next = { ...prev };
      for (const u of deployedUnits) {
        if (!next[u.id]) {
          next[u.id] = { condition: u.condition || 'good', notes: '', location_note: '', selected: false };
        }
      }
      return next;
    });
  }, [deployedUnits]);

  const fetchDeployedUnits = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['contract-deployed-units', contractId] });
  }, [queryClient, contractId]);

  useRealtimeEvents(useCallback((e) => {
    if (e.model === 'warehouses.itemunit') fetchDeployedUnits();
  }, [fetchDeployedUnits]));

  const updateField = (unitId, field, value) => {
    setReturnData(prev => ({
      ...prev,
      [unitId]: { ...prev[unitId], [field]: value },
    }));
  };

  const toggleSelect = (unitId) => {
    setReturnData(prev => ({
      ...prev,
      [unitId]: { ...prev[unitId], selected: !prev[unitId]?.selected },
    }));
  };

  const toggleSelectAll = () => {
    const allSelected = deployedUnits.every(u => returnData[u.id]?.selected);
    setReturnData(prev => {
      const next = { ...prev };
      for (const u of deployedUnits) {
        next[u.id] = { ...next[u.id], selected: !allSelected };
      }
      return next;
    });
  };

  const handleReturn = async (unitIds) => {
    setReturning(true);
    setMessage(null);
    try {
      const returns = unitIds.map(uid => ({
        unit_id: uid,
        condition: returnData[uid]?.condition || 'good',
        notes: returnData[uid]?.notes || '',
        location_note: returnData[uid]?.location_note || '',
      }));
      const res = await api.post(`contracts/api/contracts/${contractId}/return_items/`, { returns });
      const data = res.data || res;
      const errors = data.errors || [];
      if (errors.length) {
        setMessage({ type: 'warning', text: errors.map(e => `#${e.unit_id}: ${e.error}`).join('; ') });
      } else {
        setMessage({ type: 'success', text: t('contracts.return.success') });
      }
      await fetchDeployedUnits();
    } catch {
      setMessage({ type: 'danger', text: t('contracts.return.error') });
    } finally {
      setReturning(false);
    }
  };

  const handleReturnSelected = () => {
    const ids = deployedUnits.filter(u => returnData[u.id]?.selected).map(u => u.id);
    if (ids.length) handleReturn(ids);
  };

  const handleReturnAll = () => {
    handleReturn(deployedUnits.map(u => u.id));
  };

  const selectedCount = deployedUnits.filter(u => returnData[u.id]?.selected).length;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0">{t('contracts.return.title')}</h5>
          <p className="text-muted small mb-0">{t('contracts.return.description')}</p>
        </div>
        <div className="d-flex gap-2">
          {selectedCount > 0 && (
            <Button
              variant="outline-success"
              size="sm"
              onClick={handleReturnSelected}
              disabled={returning}
            >
              {returning ? <Spinner size="sm" className="me-1" /> : <i className="ti ti-arrow-back-up me-1"></i>}
              {t('contracts.return.returnBtn')} ({selectedCount})
            </Button>
          )}
          <Button
            variant="success"
            size="sm"
            onClick={handleReturnAll}
            disabled={returning || !deployedUnits.length}
          >
            {returning ? <Spinner size="sm" className="me-1" /> : <i className="ti ti-arrow-back-up me-1"></i>}
            {t('contracts.return.returnAll')}
          </Button>
        </div>
      </div>

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {loading && <div className="text-center py-3"><Spinner size="sm" /></div>}

      {!loading && !deployedUnits.length && (
        <p className="text-muted">{t('contracts.return.noUnits')}</p>
      )}

      {!loading && deployedUnits.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={deployedUnits.every(u => returnData[u.id]?.selected)}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>{t('contracts.return.itemName')}</th>
                <th>{t('contracts.return.serial')}</th>
                <th>{t('contracts.return.condition')}</th>
                <th>{t('contracts.return.locationNote')}</th>
                <th>{t('contracts.return.notes')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {deployedUnits.map((unit) => {
                const rd = returnData[unit.id] || {};
                return (
                  <tr key={unit.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!rd.selected}
                        onChange={() => toggleSelect(unit.id)}
                      />
                    </td>
                    <td>{unit.item_name}</td>
                    <td>
                      <code className="small">{unit.serial_number || unit.qr_code}</code>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        style={{ minWidth: 120 }}
                        value={rd.condition || 'good'}
                        onChange={(e) => updateField(unit.id, 'condition', e.target.value)}
                      >
                        {CONDITION_OPTIONS.map(c => (
                          <option key={c} value={c}>
                            {t(`contracts.return.conditions.${c}`)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ minWidth: 100 }}
                        value={rd.location_note || ''}
                        onChange={(e) => updateField(unit.id, 'location_note', e.target.value)}
                        placeholder={t('contracts.return.locationNote')}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ minWidth: 120 }}
                        value={rd.notes || ''}
                        onChange={(e) => updateField(unit.id, 'notes', e.target.value)}
                        placeholder={t('contracts.return.notes')}
                      />
                    </td>
                    <td>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => handleReturn([unit.id])}
                        disabled={returning}
                      >
                        {t('contracts.return.returnBtn')}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <ContractCases contractId={contractId} mode="return" />
      </div>
    </div>
  );
};

export default ContractReturn;
